import { type VariantProps, cva } from "class-variance-authority";
import { type ChangeEvent, useRef } from "react";
import { cn } from "../../lib/utils";

const OTPVariants = cva(
	"border text-black w-12 h-14 focus:border-2 text-center outline-none rounded",
	{
		variants: {
			variant: {
				default: "focus:border-black",
				error: "border-red-500 focus:border-red-500",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

interface OTPProps extends VariantProps<typeof OTPVariants> {
	length: number;
	onComplete: (pin: string) => Promise<void>;
	formData: string[];
	handleChange: (e: ChangeEvent<HTMLInputElement>, index: number) => void;
}

export const OTPInputPassword = ({
	formData,
	handleChange,
	length,
	onComplete,
	variant,
}: OTPProps) => {
	const inputRefs = useRef<(HTMLInputElement | null)[]>(
		Array.from({ length }, () => null),
	);

	function handleTextChange(input: string, index: number) {
		const numericInput = input.replace(/[^0-9]/g, "");

		handleChange(
			{
				target: { name: `otp${index}`, value: numericInput },
			} as ChangeEvent<HTMLInputElement>,
			index,
		);

		if (numericInput.length === 1 && index < length - 1) {
			inputRefs.current[index + 1]?.focus();
		}

		if (numericInput.length === 0 && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}

		const updatedOtp = [...formData];
		updatedOtp[index] = numericInput;

		if (index === length - 1 && updatedOtp.every((digit) => digit !== "")) {
			onComplete(updatedOtp.join(""));
		}
	}

	return (
		<div className="flex items-center gap-3">
			{Array.from({ length }, (_, index) => (
				<input
					key={index}
					type="text"
					maxLength={1}
					value={formData[index] || ""}
					onChange={(e) => handleTextChange(e.target.value, index)}
					ref={(el) => {
						inputRefs.current[index] = el;
						return el;
					}}
					className={cn(OTPVariants({ variant }))}
					style={{ marginRight: index === length - 1 ? "0" : "10px" }}
				/>
			))}
		</div>
	);
};
